/* eslint-disable max-lines-per-function */

import './Global.scss';
import NavigationMenu from 'extensions/navigation-menu';
import ContinueShopping from 'extensions/continue-shopping';
import {PageManager} from '@mvps-genesis/bootstrap';
import {GlobalMessageContainer} from 'extensions/messages';
import MiniBasket from 'extensions/mini-basket';
import {initCarousels} from 'extensions/carousel';
import dynamicFormElementAttributes from 'extensions/dynamic-form-element-attributes';
import WebFont from 'webfontloader';
import ConfirmAction from 'extensions/confirm-action';
import {SkipToSection, MenuBar} from '@mvps-genesis/accessibility';
import {Autocomplete} from 'custom-extensions/searchspring';
import ProductCompare from 'extensions/product-compare/ProductCompare';

export default class Global extends PageManager {

	// eslint-disable-next-line max-lines-per-function
	constructor (pageContext) {

		super(pageContext);

		this.initSkipToSection();

		WebFont.load({
			typekit: {
				id: 'een7zfr'
			}
		});

		this.initSearchPreview();

		/**
		 * Create global message container
		 */
		new GlobalMessageContainer({container: '#js-messages'});

		/**
		 * Initialize carousels
		 */
		initCarousels();

		this.initializeHTMLFormValidator();
		this.initializeSubmitEvents();

		/**
		 * Initialize the Dynamic Form-Element Attributes
		 */
		dynamicFormElementAttributes();

		this.initModals();
		this.initLazyload();
		this.initCookieConsent();
		this.initAddressEvents();
		this.initScrollTo();
		this.initBackToTop();
		this.initStickyListener();
		this.initAjaxLoginRegister();
		this.initFormLoadingUI();
		this.initTabbedCarousels();
		this.initGTM();
		this.initProductCompare();
		this.switchAreaProductLayout();

	}

	// eslint-disable-next-line max-lines-per-function
	onReady () {

		/**
		 * Initialize the Navigation Menu extension
		 */
		const mainMenuElem = document.querySelector('.js-navigation-menu');
		if (mainMenuElem) {
			this.navigationMenu = new NavigationMenu({
				drawers: '.js-navigation-menu__drawer',
				mainMenu: mainMenuElem,
				mainMenuCloseButtons: '.js-navigation-menu__close',
				mainMenuOpenButtons: '.js-navigation-menu__open',
				menuItems: '.js-navigation-menu__link',
				parentMenus: '.js-navigation-menu__has-child',
				showPreviousMenuButtons: '.js-navigation-menu__previous'
			});
		}

		const accountLinks = document.getElementById('js-customer-account-links');
		if (accountLinks) {
			// Basic instance constructor
			new MenuBar({
				breakpoint: false,
				element: accountLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		const staticLinks = document.getElementById('js-static-page-links');
		if (staticLinks) {
			// Basic instance constructor
			new MenuBar({
				element: staticLinks,
				navigateOnDown: true,
				navigateOnEnter: true,
				navigateOnSpace: true
			});
		}

		/**
		 * Initialize the Mini-Basket extension
		 */
		if (document.querySelector('.js-mini-basket')) {
			new MiniBasket({
				openOnAddToCart: true
			});
		}

		/**
		 * Initialize continue shopping link
		 */
		new ContinueShopping();
		this.searchFormEvents();
		this.triggerPrint();
		this.initCollapsibleMenu();
		this.initBonfireModal();

		new ConfirmAction();

	}

	initSkipToSection () {
		const sections = [];

		const menuElem = document.querySelector('.js-navigation-menu');

		if (menuElem) {
			sections.push({
				label: 'Menu',
				targetElement: menuElem
			});
		}

		const searchElem = document.querySelector('[itemprop="query-input"]');

		if (searchElem) {
			sections.push({
				label: 'Search',
				targetElement: searchElem
			});
		}

		const facetsElem = document.getElementById('js-facets');

		if (facetsElem) {
			sections.push({
				label: 'Filters',
				targetElement: facetsElem
			});
		}

		sections.push({
			label: 'Content',
			targetElement: '.g-main-content-element'
		});

		sections.push({
			label: 'Footer',
			targetElement: '.g-site-footer'
		});

		SkipToSection.createFromArray(sections);
	}

	initSearchPreview () {
		// Initialize Searchspring Autocomplete UI on global search forms
		const targetSearchPreviewElements = [
			{
				containerElement: '.g-site-header__search-form-container',
				elementId: 'autocomplete-main',
				searchBarElements: '.js-search-form-input',
				searchButtonElements: '.g-site-header__search-button'
			},
			{
				containerElement: '#js-search-form-modal',
				elementId: 'autocomplete-main-modal',
				searchBarElements: '.js-search-form-input',
				searchButtonElements: '.g-site-header__search-button'
			}
		];

		targetSearchPreviewElements.forEach((previewElement) => {
			const appElement = document.getElementById(previewElement.elementId);
			const containerElement = document.querySelector(previewElement.containerElement);

			if (!appElement || !containerElement) {
				return;
			}

			Autocomplete.create({
				appElement: appElement,
				containerElement: containerElement,
				defaultSearchParams: {
					disableSpellCorrect: false,
					resultsPerPage: 6
				},
				defaultAutocompleteParams: {
					disableSpellCorrect: false
				},
				searchBarElements: previewElement.searchBarElements,
				searchButtonElements: previewElement.searchButtonElements,
				siteId: this.pageContext.SS_Site_ID
			});
		});
	}

	initBonfireModal () {
		const modalElement = document.getElementById('js-bonfire-modal');

		if (!modalElement || typeof window.MicroModal !== 'object') {
			return;
		}

		const baseDomain = (hostname) => {
			return hostname.split('.')
				.slice(-2)
				.join('.');
		};

		let bonfireDomain = null;

		try {
			bonfireDomain = baseDomain(new URL(this.pageContext.Bonfire_Store_Url).hostname);
		} catch (error) {
			// Invalid bonfire domain
		}

		if (!bonfireDomain) {
			return;
		}

		const suppressGhostClick = () => {
			const handler = (e) => {
				e.preventDefault();
				e.stopPropagation();
			};

			document.addEventListener('click', handler, {
				capture: true,
				once: true
			});

			setTimeout(() => document.removeEventListener('click', handler, {capture: true}), 100);
		};

		const navigationMenu = this.navigationMenu;

		document.querySelectorAll('a[href]')?.forEach((link) => {
			if (modalElement.contains(link)) {
				return;
			}

			try {
				if (baseDomain(new URL(link.href).hostname) === bonfireDomain) {
					link.addEventListener('click', function (e) {
						e.preventDefault();
						e.stopImmediatePropagation();

						if (navigationMenu?.isMobile()) {
							navigationMenu.mainMenuClose();
						}

						window.MicroModal.show(modalElement.id, {
							onClose: suppressGhostClick
						});
					});
				}
			} catch (err) {
				// Invalid a[href]
			}
		});

		const shopApparelBtn = document.querySelector('.js-shop-apparel');

		if (shopApparelBtn) {
			shopApparelBtn.addEventListener('click', () => {
				setTimeout(() => window.MicroModal.close(modalElement.id), 0);
			});
		}
	}

	async initLazyload () {
		// Elements
		const lazyLoadElements = document.getElementsByClassName('lazyload');

		if (lazyLoadElements.length === 0) {
			return;
		}

		// Module
		await import('lazysizes/plugins/unveilhooks/ls.unveilhooks');
		await import('lazysizes/plugins/bgset/ls.bgset');
		await import('lazysizes/plugins/custommedia/ls.custommedia');
		await import('lazysizes');
	}

	async initCookieConsent () {
		const cookieName = `mm5-${this.pageContext.Store_Code}-genesis-cookie-consent`;

		if (document.cookie.indexOf(cookieName) !== -1) {
			return;
		}

		const {CookieConsent} = await import('extensions/cookie-consent');

		let contentLink = 'https://www.cookiesandyou.com/';
		let contentLinkTarget = '_blank';

		if (this.pageContext.Privacy_Policy_Url) {
			contentLink = this.pageContext.Privacy_Policy_Url;
			contentLinkTarget = '';
		}

		new CookieConsent({
			content: {
				dismiss: 'Got it!',
				href: contentLink,
				link: 'Learn More',
				message: 'This website uses cookies to ensure you get the best experience on our website.',
				target: contentLinkTarget
			},
			cookie: {
				name: cookieName,
				secure: true
			},
			palette: {
				button: {
					background: '#F9F9F9',
					text: '#000000'
				},
				popup: {
					background: '#222222',
					text: '#FFFFFF'
				}
			}
		});
	}

	async initializeHTMLFormValidator () {
		const forms = document.getElementsByClassName('js-form-validator');

		if (forms.length === 0) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		for (const form of forms) {
			form.HTMLFormValidator = new HTMLFormValidator({
				fieldContainer: '.g-form-list__item',
				formElement: form
			});
		}
	}

	async initializeSubmitEvents () {
		const forms = document.getElementsByClassName('js-submit-loader');

		if (forms.length === 0) {
			return;
		}

		const {FormSubmitEvents} = await import('extensions/form-submit-events');

		for (const form of forms) {
			form.submitLoaderEvent = new FormSubmitEvents({
				formElement: form
			});
		}
	}

	async initModals () {
		const hasModalTrigger = document.querySelector(`[data-micromodal-trigger]`);

		if (!hasModalTrigger) {
			return;
		}

		const {default: Modal} = await import('extensions/modal');
		const modal = new Modal();

		/**
		 * Initialize modals:
		 * 	Pass list of modal ids without an overlay that should close when
		 * 	clicking off of modal.
		 */
		modal.init([
			'js-search-form-modal',
			'js-global-account'
		]);
	}

	async initAddressEvents () {
		const datalists = document.querySelectorAll('[data-address-datalist]');
		const toggles = document.querySelectorAll('[data-address-toggle]');

		if (datalists.length === 0 && toggles.length === 0) {
			return;
		}

		const {AddressEvents} = await import('extensions/address-events');

		new AddressEvents(datalists, toggles);
	}

	// eslint-disable-next-line max-lines-per-function
	searchFormEvents () {
		const searchForms = document.getElementsByClassName('js-search-form');

		if (!searchForms.length) {
			return;
		}

		const clearButtonToggleClass = 'is-visible';
		const formFocusClass = 'is-focused';

		for (const form of searchForms) {
			const searchInput = form.querySelector('.js-search-form-input');

			if (!searchInput) {
				continue;
			}

			const searchClearButton = form.querySelector('.js-site-header__search-clear');

			searchInput.addEventListener('focus', () => {
				form.classList.add(formFocusClass);
			});

			searchInput.addEventListener('blur', (event) => {
				if (searchClearButton) {
					if (event.relatedTarget === searchClearButton) {
						return;
					}

					if (searchInput.value === '') {
						searchClearButton.classList.remove(clearButtonToggleClass);
						searchClearButton.setAttribute('aria-hidden', 'true');
					}
				}

				if (event.relatedTarget === form || form.contains(event.relatedTarget)) {
					return;
				}

				form.classList.remove(formFocusClass);
			});

			if (searchClearButton) {
				searchClearButton.addEventListener('click', () => {
					searchInput.value = '';
					searchClearButton.classList.remove(clearButtonToggleClass);
					searchClearButton.setAttribute('aria-hidden', 'true');
					searchInput.focus();
				});

				searchInput.addEventListener('input', () => {
					searchClearButton.classList.toggle(clearButtonToggleClass, searchInput.value);

					if (searchClearButton.classList.contains(clearButtonToggleClass)) {
						searchClearButton.setAttribute('aria-hidden', 'false');
					} else {
						searchClearButton.setAttribute('aria-hidden', 'true');
					}
				});
			}
		}
	}

	triggerPrint () {

		/**
		 *  Launch Printer Dialog
		 */
		const printTriggers = document.getElementsByClassName('js-trigger-print');

		if (printTriggers.length) {
			for (const element of printTriggers) {
				element.addEventListener('click', function (e) {
					e.preventDefault();
					window.print();
				}, false);
			}
		}

	}

	async initScrollTo () {
		const triggerElements = document.getElementsByClassName('js-scroll-to');

		if (!triggerElements.length) {
			return;
		}

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo(triggerElements, {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				block: 'end',
				inline: 'nearest'
			}
		});
	}

	async initBackToTop () {

		const backToTopButton = document.getElementById('js-back-to-top-button');

		if (!backToTopButton) {
			return;
		}

		const backToTopClass = 'g-back-to-top-button--shown';

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo('#js-back-to-top-button', {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				inline: 'nearest'
			}
		});

		let lastScrollTop = 0;

		window.addEventListener('scroll', () => {
			this.showHideBackToTop(backToTopButton, backToTopClass);

			// Detect if user is scrolling up or down
			let st = window.pageYOffset || document.documentElement.scrollTop;
			if (st > lastScrollTop) {
				document.body.classList.remove('g-user-scrolling-up');
			} else {
				document.body.classList.add('g-user-scrolling-up');
			}

			lastScrollTop = st <= 0 ? 0 : st;
		});

		this.showHideBackToTop(backToTopButton, backToTopClass);

	}

	showHideBackToTop (backToTopButton, backToTopClass) {

		const heightToShowButton = window.innerHeight;
		const offset = heightToShowButton / 2;
		const windowOffset = offset + (isNaN(window.pageYOffset) ? 0 : window.pageYOffset);

		if (windowOffset >= heightToShowButton) {
			backToTopButton.classList.add(backToTopClass);
		} else {
			backToTopButton.classList.remove(backToTopClass);
		}

	}

	initStickyListener () {
		if (!('IntersectionObserver' in window)) {
			return;
		}

		const header = document.querySelector('.g-site-header:not(.g-site-header__unsticky)');

		if (!header) {
			return;
		}

		const observer = new IntersectionObserver(
			([e]) => e.target.classList.toggle('g-site-header__sticky', e.intersectionRatio < 1),
			{threshold: [1]}
		);

		observer.observe(header);
	}

	async initAjaxLoginRegister () {
		const loginRegisterElement = document.getElementById('js-login-register');

		if (!loginRegisterElement) {
			return;
		}

		const {AjaxLoginRegister} = await import('extensions/ajax-login-register');

		new AjaxLoginRegister(loginRegisterElement);
	}

	async initFormLoadingUI () {
		const formElements = document.getElementsByClassName('js-form-show-loading');

		if (!formElements.length) {
			return;
		}

		const {default: LoadingUI} = await import('extensions/loading-ui');
		const loadingContainerSelector = '.js-loading-container';
		const loadingElementSelector = '.js-loading-element';

		for (const formElement of formElements) {
			const loadingContainerElement = formElement.closest(loadingContainerSelector);

			if (!loadingContainerElement) {
				continue;
			}

			const loadingElement = loadingContainerElement.querySelector(loadingElementSelector);

			if (!loadingElement) {
				continue;
			}

			formElement.LoadingUI = new LoadingUI(loadingElement, {
				overlayElement: loadingContainerElement.hasAttribute('data-disable-loading-overlay') ? false : loadingContainerElement
			});

			formElement.addEventListener('submit', () => {
				formElement.LoadingUI.show();
			});
		}
	}

	async initCollapsibleMenu () {

		const menuElements = document.getElementsByClassName('js-collapsible-menu');

		if (menuElements.length === 0) {
			return;
		}

		const {default: CollapsibleMenu} = await import('extensions/collapsible-menu');

		new CollapsibleMenu(menuElements);

	}

	async initTabbedCarousels () {

		const tabbedCarouselElements = document.getElementsByClassName('js-tabbed-carousel');

		if (!tabbedCarouselElements.length) {
			return;
		}

		const {default: TabbedCarousels} = await import('extensions/tabbed-carousels');

		new TabbedCarousels(tabbedCarouselElements);

	}

	async initGTM () {

		const {default: GTM} = await import('extensions/gtm');

		const gtm = new GTM(this.pageContext);
		gtm.init();

		// Listen for Print Events
		window.addEventListener('beforeprint', () => {
			gtm.sendEvent({
				action: 'Click',
				category: 'Print',
				label: document.title,
				noninteractionHit: false
			});
		});
	}
	async switchAreaProductLayout () {
		const wrapper = document.querySelector('.g-wrapper.g-product-description');
  		const details = document.querySelector('.g-product-details-section');
  		const tabs = document.querySelector('.g-product-tabcordion-section');

  		if (!wrapper || !details || !tabs) return;

		function reorderSections() {
			if (window.innerWidth <= 767) {
				if (tabs.previousElementSibling !== details) {
					wrapper.insertBefore(details, tabs);
				}
			} else {
				if (details.previousElementSibling !== tabs) {
					wrapper.insertBefore(tabs, details);
				}
			}
		}

		reorderSections();
  		window.addEventListener('resize', reorderSections);
	}
	async initProductCompare () {
		const waitForElements = () => {
			return new Promise((resolve) => {
				const check = () => {
					const prodCmpContainer = document.getElementById('js-compare-container');
					const prodCmpSelElements = document.getElementsByClassName('js-compare-select-container');

					if (prodCmpContainer && prodCmpSelElements.length && typeof this.pageContext.Japi_Url === 'string') {
						resolve({prodCmpContainer, prodCmpSelElements});
					} else {
						setTimeout(check, 100);
					}
				};

				check();
			});
		};

		const {prodCmpContainer, prodCmpSelElements} = await waitForElements();

		for (const cmpElement of prodCmpSelElements) {
			cmpElement.classList.remove('u-hidden');
		}

		prodCmpContainer.classList.remove('u-hidden');

		new ProductCompare({url: this.pageContext.Japi_Url});
	}
}
